import { Link } from 'react-router'
import { Building2, CheckCircle2, Circle } from 'lucide-react'
import { TASK_STATUSES } from '@shared/enums'
import { useUpdateTask } from '../../hooks/useTaskMutations'
import type { Task } from '../../lib/api'

/**
 * The edit controls shared by every surface that shows a task — board card,
 * list row, client tab, detail panel. They exist once so that "change the
 * status" means the same thing and looks the same wherever the workflow
 * happens to start.
 */

export const STATUS_LABELS: Record<string, string> = {
  todo: 'To Do',
  in_progress: 'In Progress',
  review: 'Review',
  done: 'Done',
  blocked: 'Blocked',
}

/** Inline status dropdown. Writes through on change, no save step. */
export function StatusSelect({
  task,
  disabled,
  className,
}: {
  task: Pick<Task, 'id' | 'status'>
  disabled?: boolean
  className?: string
}) {
  const update = useUpdateTask()

  return (
    <select
      className={`status-select status-select--${task.status}${className ? ` ${className}` : ''}`}
      value={task.status}
      disabled={disabled}
      aria-label="Task status"
      // Rows and cards are click-to-open; the dropdown is a control inside
      // them, so its clicks must not also open the card.
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => {
        e.stopPropagation()
        update.mutate({ id: task.id, patch: { status: e.target.value } })
      }}
    >
      {TASK_STATUSES.map((s) => (
        <option key={s} value={s}>
          {STATUS_LABELS[s] ?? s}
        </option>
      ))}
    </select>
  )
}

/**
 * One-click complete. Toggles done ↔ todo without opening the task.
 *
 * Shared by the list, the Today lists and the client tab: "mark it done" is the
 * most frequent edit there is, and it should not depend on which screen you
 * happened to be looking at when you finished the work.
 */
export function CompleteToggle({
  task,
  disabled,
  size = 18,
  className,
}: {
  task: Pick<Task, 'id' | 'status'>
  disabled?: boolean
  size?: number
  className?: string
}) {
  const update = useUpdateTask()
  const done = task.status === 'done'

  return (
    <button
      type="button"
      className={className ?? 'task-col-check'}
      disabled={disabled}
      aria-label={done ? 'Mark incomplete' : 'Mark done'}
      // Rows are click-to-open; completing one must not also open it.
      onClick={(e) => {
        e.stopPropagation()
        update.mutate({ id: task.id, patch: { status: done ? 'todo' : 'done' } })
      }}
    >
      {done ? (
        <CheckCircle2 size={size} className="text-success" />
      ) : (
        <Circle size={size} className="text-muted" />
      )}
    </button>
  )
}

/**
 * Inline discipline picker. Writes through on change, like status and due.
 *
 * The task's own value is always an option, even when the tenant has since
 * removed or renamed that discipline. This mirrors `checkDiscipline` accepting
 * the record's `current` value server-side: without it, a task holding a stale
 * string would render as whichever option happened to sit first and silently
 * re-file itself on the next unrelated save.
 */
export function DisciplineSelect({
  task,
  disciplines,
  disabled,
  className,
}: {
  task: Pick<Task, 'id' | 'discipline'>
  disciplines: string[]
  disabled?: boolean
  className?: string
}) {
  const update = useUpdateTask()
  const options = disciplines.includes(task.discipline)
    ? disciplines
    : [task.discipline, ...disciplines]

  return (
    <select
      className={`discipline-select${className ? ` ${className}` : ''}`}
      value={task.discipline}
      disabled={disabled}
      aria-label="Discipline"
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => {
        e.stopPropagation()
        update.mutate({ id: task.id, patch: { discipline: e.target.value } })
      }}
    >
      {options.map((d) => (
        <option key={d} value={d}>
          {d}
        </option>
      ))}
    </select>
  )
}

/** Inline due-date picker. Clearing the field clears the date. */
export function DueDateInput({
  task,
  disabled,
  className,
}: {
  task: Pick<Task, 'id' | 'due'>
  disabled?: boolean
  className?: string
}) {
  const update = useUpdateTask()
  const today = new Date().toISOString().slice(0, 10)
  const isOverdue = !!task.due && task.due < today

  return (
    <input
      type="date"
      className={`due-input${isOverdue ? ' due-input--overdue' : ''}${
        // An empty date input renders a literal "mm/dd/yyyy", which on a list
        // where most tasks are undated is a column of noise. Hidden until the
        // row is hovered or the field focused — the calendar glyph carries the
        // affordance in the meantime.
        task.due ? '' : ' due-input--empty'
      }${className ? ` ${className}` : ''}`}
      value={task.due ?? ''}
      disabled={disabled}
      aria-label="Due date"
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => {
        e.stopPropagation()
        update.mutate({ id: task.id, patch: { due: e.target.value || null } })
      }}
    />
  )
}

/**
 * The client a task belongs to — the single most-requested thing on a card.
 * Links through to the client when the viewer has the Clients module; renders
 * as plain text otherwise, so a contractor still knows whose work this is
 * without being handed a dead link.
 */
export function ClientLabel({
  clientId,
  clientName,
  linkTo,
  className,
}: {
  clientId: string | null
  clientName: string | null
  linkTo?: boolean
  className?: string
}) {
  if (!clientName) return null

  const cls = `client-label${className ? ` ${className}` : ''}`
  const inner = (
    <>
      <Building2 size={12} />
      <span>{clientName}</span>
    </>
  )

  if (linkTo && clientId) {
    return (
      <Link to={`/clients/${clientId}`} className={`${cls} client-label--link`} onClick={(e) => e.stopPropagation()}>
        {inner}
      </Link>
    )
  }

  return <span className={cls}>{inner}</span>
}
